import { Component, inject, OnInit, signal } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { WalletTransaction } from '../../../models/wallet-transaction';
import { displayStatus, operationType, WalletTransactionType } from '../../../models/enums';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { TransactionService } from '../../../services/transactions/transaction-service';

@Component({
  selector: 'app-wallet-history-list',
  imports: [TableComponent],
  templateUrl: './wallet-history-list.html',
  styleUrl: './wallet-history-list.scss'
})
export class WalletHistoryList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'transactionType',
    'operationType',
    'displayStatus',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    transactionType: "transactionType",
    operationType: "operationType",
    displayStatus: "displayStatus",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<WalletTransaction>[] = [
    { header: 'ID', field: 'id', sortable: false },
    { header: 'Created At', field: 'createdAt', sortable: false, isDateTime: true },
    {
      header: 'Transaction Type',
      field: 'transactionType',
      sortable: false,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'select',
      filterOptions: enumToOptions(WalletTransactionType),
      cellClass: (value) => {
        if (value === WalletTransactionType.ADMIN_CREDIT) return 'text-green';
        if (value === WalletTransactionType.ADMIN_DEBIT) return 'text-red';
        return '';
      }
    },
    { header: 'Created By', field: 'createdByEmployeeName', sortable: false },
    { header: 'Balance', field: 'balanceAfter', sortable: true, suffixText: "AED" },
  ];

  walletTransactionHistoryList = signal<WalletTransaction[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  operationTypeFilter: CustomFilter = {
    key: "operationType",
    label: "Operation type",
    type: "select",
    options: enumToOptions(operationType)
  }

  displayStatusFilter: CustomFilter = {
    key: "displayStatus",
    label: "Display status",
    type: "select",
    options: enumToOptions(displayStatus)
  }

  private transactionService = inject(TransactionService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getWalletTransactionHistoryList()
  }

  getWalletTransactionHistoryList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.transactionService.getWalletTransactionsList(params);
      })
    )
      .subscribe((res) => {
        this.walletTransactionHistoryList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }
}
